import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserId, getProspectId, openProfile, selectPreferredLanguage, setLogout } from "features/authSlice";
import { useNavigate } from "react-router-dom";

import "./header-menu.scss";
import useDesktopChecker from "hooks/useDesktopChecker";
import DropDownMenu from "../DropDownMenu/DropDownMenu";
import ToastMessage from "../toast-message/ToastMessage";
import { complianceApi } from "services/common/complianceService";
import { useUpdateDashboardSessionEndMutation } from "pages/dashboard/home/dashboardLogoutService";
import { DASHBOARD_SESSION_ID, DASHBOARD_SESSION_START_TIMESTAMP } from "utilities/Constant";
import { usePostLogoutMutation } from "pages/login/logoutService";
import { deleteAllFiles } from "utilities/files";
import { getLocalizedPreferenceCenterLink } from "utilities/getLocalizedPreferenceCenterLink";


export interface HeaderMenuProps {
  name?: string;
  onShow?: boolean;
  showPreferences: boolean;
  showNavbar: () => void;
}

export default function HeaderMenu({ name, onShow, showPreferences, showNavbar }: Readonly<HeaderMenuProps>) {
  const [checked, setChecked] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [showErrToast, setShowErrToast] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const complianceId = useSelector(getProspectId);
  const preferredLanguage = useSelector(selectPreferredLanguage);
  const [invokePreferenceCenterUrl] = complianceApi.endpoints.getPreferencesUrl.useLazyQuery();
  const isDesktop = useDesktopChecker();
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const userId = useSelector(getCurrentUserId);
  const dashboardSessionId = sessionStorage.getItem(DASHBOARD_SESSION_ID);
  const [updateLogout] = usePostLogoutMutation();

  const [updateSessionEnd] = useUpdateDashboardSessionEndMutation();
  const handleChange = () => {
    setChecked(prev => !prev);
    setShowPopUp(prev => !prev);
  };

  const onClickLogout = async () => {
    await deleteAllFiles(); 
    void updateLogout();
    if (dashboardSessionId) {
      void updateSessionEnd({
        userId,
        dashboardSession: {
          dashboardSessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID) ?? "",
          dashboardSessionStartTimestamp: sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP),
          dashboardSessionEndTimestamp: new Date().toISOString(),
        }
      }).unwrap();
      sessionStorage.clear();
    }
    dispatch(setLogout());
    navigate("/");
  };

  const onClickPreference = useCallback(async () => {
    try {
      const { data } = await invokePreferenceCenterUrl(complianceId);
      if (data?.preferenceCenterLink) {
        globalThis.open(
          getLocalizedPreferenceCenterLink(data.preferenceCenterLink, preferredLanguage),
          "_blank",
        );
      } else {
        setShowErrToast(true);
      }
    } catch (err) {
      console.error(err);
      setShowErrToast(true);
    }
  }, [complianceId, invokePreferenceCenterUrl, preferredLanguage]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPopUp(false);
        setChecked(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    if (showErrToast) {
      const timer = setTimeout(() => {
        setShowErrToast(false);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [showErrToast]);

  return (
    <>
      {showErrToast && !showPopUp && (
        <ToastMessage
          header="Link Unavailable"
          message="Please try again later or contact support"
          state="error"
          timeout={4000}
          position={isDesktop ? "top-center" : "bottom-center"}
        />
      )}
      {isDesktop && (
        <button type="button" className="header-menu" onClick={handleChange} ref={dropdownRef}>
          <span className="header-menu__description" title={name}>{name}</span>
          <span className="header-menu__icon">
            {showPopUp ? <FontAwesomeIcon icon={solid("caret-up")} /> : <FontAwesomeIcon icon={solid("caret-down")} />}
          </span>
        </button>
      )}
      {(onShow || showPopUp) && (
        <DropDownMenu
          onShow={checked || onShow}
          onClickLogout={onClickLogout}
          showNavbar={showNavbar}
          showPreferences={showPreferences}
          onClickPreference={onClickPreference}
          onClickProfile={() => dispatch(openProfile())}
        />
      )}
    </>
  );
}
